import { NexusRoutes } from '../routes';
import { BaseAPIClient } from './BaseAPIClient';
import {
  AcceptOrderAcknowledgementRequest,
  AcceptOrderAcknowledgementResponse,
  CreateInvestmentAccountRequest,
  CreateInvestmentAccountResponse,
  CreateManualPaymentMethodRequest,
  CreateManualPaymentMethodResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  CreatePlaidBankAccountRequest,
  CreatePlaidBankAccountResponse,
  DeletePaymentMethodRequest,
  DeletePaymentMethodResponse,
  FetchAccountDocumentsRequest,
  FetchAccountDocumentsResponse,
  FetchAccountHoldingsRequest,
  FetchAccountHoldingsResponse,
  FetchAccountPaymentMethodsRequest,
  FetchAccountPaymentMethodsResponse,
  FetchAccountRequest,
  FetchAccountResponse,
  FetchAccountsRequest,
  FetchAccountsResponse,
  FetchAccountTransactionsRequest,
  FetchAccountTransactionsResponse,
  FetchBankAccountRequest,
  FetchBankAccountResponse,
  FetchEligibilityRequest,
  FetchEligibilityResponse,
  FetchIdentityVerificationRequest,
  FetchIdentityVerificationResponse,
  FetchOnboardingProgressRequest,
  FetchOnboardingProgressResponse,
  FetchOrderAcknowledgementsRequest,
  FetchOrderAcknowledgementsResponse,
  FetchOrderDocumentsRequest,
  FetchOrderDocumentsResponse,
  FetchOrderRequest,
  FetchOrderResponse,
  FetchPaymentMethodsRequest,
  FetchPaymentMethodsResponse,
  FetchPlaidLinkTokenRequest,
  FetchPlaidLinkTokenResponse,
  FetchWalletBalanceRequest,
  FetchWalletBalanceResponse,
  GetClaimRequest,
  GetClaimResponse,
  ClaimCoinRequest,
  ClaimCoinResponse,
  GetOverviewRequest,
  GetOverviewResponse,
  RequestInvitationRequest,
  RequestInvitationResponse,
  GetEcosystemOverviewRequest,
  GetEcosystemOverviewResponse,
  VerifyClaimOwnerRequest,
  VerifyClaimOwnerResponse,
  JoinWaitlistRequest,
  JoinWaitlistResponse,
  RegisterUserRequest,
  RegisterUserResponse,
  ResendEmailVerificationRequest,
  ResendEmailVerificationResponse,
  ReviewOrderRequest,
  ReviewOrderResponse,
  SetPreferredPaymentMethodRequest,
  SetPreferredPaymentMethodResponse,
  StartIdentityVerificationRequest,
  StartIdentityVerificationResponse,
  SubmitOrderRequest,
  SubmitOrderResponse,
  SyncIdentityVerificationRequest,
  SyncIdentityVerificationResponse,
  UpdateBankAccountRequest,
  UpdateBankAccountResponse,
  UpdateContactInformationRequest,
  UpdateContactInformationResponse,
  UpdateEntityInformationRequest,
  UpdateEntityInformationResponse,
  UpdateManualPaymentMethodRequest,
  UpdateManualPaymentMethodResponse,
  UpdateOrderRequest,
  UpdateOrderResponse,
} from './APIClientInterface';

export class APIClient extends BaseAPIClient {
  static async createInvestmentAccount(
    request: CreateInvestmentAccountRequest
  ): Promise<CreateInvestmentAccountResponse> {
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.Accounts, request, config)
      .then(httpResponse => {
        const response = this.prepareResponse<CreateInvestmentAccountResponse>(request, httpResponse);
        if (response.success) response.account = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<CreateInvestmentAccountResponse>(request, error));
  }

  static async createManualPaymentMethod(
    request: CreateManualPaymentMethodRequest
  ): Promise<CreateManualPaymentMethodResponse> {
    const { investmentAccountId, ...data } = request;
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.AccountPaymentMethods(investmentAccountId), data, config)
      .then(httpResponse => {
        const response = this.prepareResponse<CreateManualPaymentMethodResponse>(request, httpResponse);
        if (response.success) response.paymentMethod = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<CreateManualPaymentMethodResponse>(request, error));
  }

  static async deletePaymentMethod(request: DeletePaymentMethodRequest): Promise<DeletePaymentMethodResponse> {
    const { paymentMethodId } = request;
    const { client, config } = this.createClient();

    return client
      .delete(NexusRoutes.PaymentMethodDetail(paymentMethodId), config)
      .then(httpResponse => this.prepareResponse<DeletePaymentMethodResponse>(request, httpResponse))
      .catch(error => this.prepareResponse<DeletePaymentMethodResponse>(request, error));
  }

  static async fetchAccounts(request: FetchAccountsRequest): Promise<FetchAccountsResponse> {
    const { client, config } = this.createClient();
    const { page_size, page } = request;

    return client
      .get(`${NexusRoutes.Accounts}?page_size=${page_size}&page=${page}`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountsResponse>(request, httpResponse);
        if (response.success) {
          response.accounts = httpResponse.data.results;
          response.nextPageUrl = httpResponse.data.next;
        }

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountsResponse>(request, error));
  }

  static async fetchAccount(request: FetchAccountRequest): Promise<FetchAccountResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.Account(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountResponse>(request, httpResponse);
        if (response.success) response.account = httpResponse.data;

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountResponse>(request, error));
  }

  static async fetchAccountPaymentMethods(
    request: FetchAccountPaymentMethodsRequest
  ): Promise<FetchAccountPaymentMethodsResponse> {
    const { investmentAccountId } = request;
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.AccountPaymentMethods(investmentAccountId), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountPaymentMethodsResponse>(request, httpResponse);
        if (response.success) response.paymentMethods = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountPaymentMethodsResponse>(request, error));
  }

  static async fetchAccountHoldings(request: FetchAccountHoldingsRequest): Promise<FetchAccountHoldingsResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.AccountHoldings(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountHoldingsResponse>(request, httpResponse);
        if (response.success) response.accountHoldings = httpResponse.data;

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountHoldingsResponse>(request, error));
  }

  static async fetchWalletBalance(request: FetchWalletBalanceRequest): Promise<FetchWalletBalanceResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.WalletBalance(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchWalletBalanceResponse>(request, httpResponse);
        if (response.success) response.walletBalance = httpResponse.data;

        return response;
      })
      .catch(error => this.prepareResponse<FetchWalletBalanceResponse>(request, error));
  }

  static async fetchAccountTransactions(
    request: FetchAccountTransactionsRequest
  ): Promise<FetchAccountTransactionsResponse> {
    const { id, page, page_size } = request;
    const { client, config } = this.createClient();

    return client
      .get(`${NexusRoutes.AccountTransactions(id)}?page_size=${page_size}&page=${page}`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountTransactionsResponse>(request, httpResponse);
        if (response.success) {
          response.nextPageUrl = httpResponse.data.next;
          response.transactions = httpResponse.data.results;
        }

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountTransactionsResponse>(request, error));
  }

  static async fetchAccountDocuments(request: FetchAccountDocumentsRequest): Promise<FetchAccountDocumentsResponse> {
    const { id, page_size, category } = request;
    const { client, config } = this.createClient();

    return client
      .get(`${NexusRoutes.AccountDocuments(id)}?category=${category}&page_size=${page_size}`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountDocumentsResponse>(request, httpResponse);
        if (response.success) {
          response.nextPageUrl = httpResponse.data.next;
          response.documents = httpResponse.data.results;
        }

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountDocumentsResponse>(request, error));
  }

  static async fetchBankAccount(request: FetchBankAccountRequest): Promise<FetchBankAccountResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.BankAccount(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchBankAccountResponse>(request, httpResponse);
        if (response.success) response.bankAccount = httpResponse.data;
        return response;
      })
      .catch(error => {
        if (error.response && error.response.status === 404) {
          return this.prepareResponse<FetchBankAccountResponse>(request, error.response);
        }
        return this.prepareResponse<FetchBankAccountResponse>(request, error);
      });
  }

  static async fetchOnboardingProgress(
    request: FetchOnboardingProgressRequest
  ): Promise<FetchOnboardingProgressResponse> {
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.OnboardingProgress, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchOnboardingProgressResponse>(request, httpResponse);
        if (response.success) response.progress = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchOnboardingProgressResponse>(request, error));
  }

  static async registerUser(request: RegisterUserRequest): Promise<RegisterUserResponse> {
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.Register, request, config)
      .then(httpResponse => {
        const response = this.prepareResponse<RegisterUserResponse>(request, httpResponse);
        if (response.success) {
          response.id = httpResponse.data.id;
          response.email = httpResponse.data.email;
        }
        return response;
      })
      .catch(error => this.prepareResponse<RegisterUserResponse>(request, error));
  }

  static async resendEmailVerification(
    request: ResendEmailVerificationRequest
  ): Promise<ResendEmailVerificationResponse> {
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.ResendEmailVerification, {}, config)
      .then(httpResponse => this.prepareResponse<ResendEmailVerificationResponse>(request, httpResponse))
      .catch(error => this.prepareResponse<ResendEmailVerificationResponse>(request, error));
  }

  static async updateBankAccount(request: UpdateBankAccountRequest): Promise<UpdateBankAccountResponse> {
    const { id } = request;
    const { client, config } = this.createClient();

    return client
      .put(NexusRoutes.UpdateBankAccount(id), request, config)
      .then(httpResponse => {
        const response = this.prepareResponse<UpdateBankAccountResponse>(request, httpResponse);

        return response;
      })
      .catch(error => {
        return this.prepareResponse<UpdateBankAccountResponse>(request, error);
      });
  }

  static async updateContactInformation(
    request: UpdateContactInformationRequest
  ): Promise<UpdateContactInformationResponse> {
    const { id, ...data } = request;
    const { client, config } = this.createClient();

    return client
      .put(NexusRoutes.ContactInformation(id), data, config)
      .then(httpResponse => {
        const response = this.prepareResponse<UpdateContactInformationResponse>(request, httpResponse);
        return response;
      })
      .catch(error => this.prepareResponse<UpdateContactInformationResponse>(request, error));
  }

  static async updateEntityInformation(
    request: UpdateEntityInformationRequest
  ): Promise<UpdateEntityInformationResponse> {
    const { id, ...data } = request;
    const { client, config } = this.createClient();

    return client
      .put(NexusRoutes.EntityInformation(id), data, config)
      .then(httpResponse => {
        const response = this.prepareResponse<UpdateEntityInformationResponse>(request, httpResponse);
        return response;
      })
      .catch(error => this.prepareResponse<UpdateEntityInformationResponse>(request, error));
  }

  static async acceptOrderAcknowledgement(
    request: AcceptOrderAcknowledgementRequest
  ): Promise<AcceptOrderAcknowledgementResponse> {
    const { orderId, acknowledgementId } = request;
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.OrderAcknowledgementAccept(orderId, acknowledgementId), {}, config)
      .then(httpResponse => this.prepareResponse<AcceptOrderAcknowledgementResponse>(request, httpResponse))
      .catch(error => this.prepareResponse<AcceptOrderAcknowledgementResponse>(request, error));
  }

  static async createOrder(request: CreateOrderRequest): Promise<CreateOrderResponse> {
    const { accountId } = request;
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.AccountOrders(accountId), {}, config)
      .then(httpResponse => {
        const response = this.prepareResponse<CreateOrderResponse>(request, httpResponse);
        if (response.success) response.order = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<CreateOrderResponse>(request, error));
  }

  static async fetchEligibility(request: FetchEligibilityRequest): Promise<FetchEligibilityResponse> {
    const { accountId } = request;
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.InvestmentEligibility(accountId), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchEligibilityResponse>(request, httpResponse);
        if (response.success) response.eligibility = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchEligibilityResponse>(request, error));
  }

  static async fetchOrder(request: FetchOrderRequest): Promise<FetchOrderResponse> {
    const { orderId } = request;
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.Order(orderId), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchOrderResponse>(request, httpResponse);
        if (response.success) response.order = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchOrderResponse>(request, error));
  }

  static async fetchOrderAcknowledgements(
    request: FetchOrderAcknowledgementsRequest
  ): Promise<FetchOrderAcknowledgementsResponse> {
    const { orderId } = request;
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.OrderAcknowledgements(orderId), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchOrderAcknowledgementsResponse>(request, httpResponse);
        if (response.success) response.acknowledgements = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchOrderAcknowledgementsResponse>(request, error));
  }

  static async fetchOrderDocuments(request: FetchOrderDocumentsRequest): Promise<FetchOrderDocumentsResponse> {
    const { orderId } = request;
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.OrderDocuments(orderId), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchOrderDocumentsResponse>(request, httpResponse);
        if (response.success) response.documents = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchOrderDocumentsResponse>(request, error));
  }

  static async fetchPaymentMethods(request: FetchPaymentMethodsRequest): Promise<FetchPaymentMethodsResponse> {
    const { orderId } = request;
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.OrderAvailablePaymentMethods(orderId), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchPaymentMethodsResponse>(request, httpResponse);
        if (response.success) response.payment_methods = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchPaymentMethodsResponse>(request, error));
  }

  static async reviewOrder(request: ReviewOrderRequest): Promise<ReviewOrderResponse> {
    const { orderId } = request;
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.OrderReview(orderId), {}, config)
      .then(httpResponse => {
        const response = this.prepareResponse<ReviewOrderResponse>(request, httpResponse);
        if (response.success) response.order = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<ReviewOrderResponse>(request, error));
  }

  static async submitOrder(request: SubmitOrderRequest): Promise<SubmitOrderResponse> {
    const { orderId } = request;
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.OrderSubmit(orderId), {}, config)
      .then(httpResponse => {
        const response = this.prepareResponse<SubmitOrderResponse>(request, httpResponse);
        if (response.success) response.order = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<SubmitOrderResponse>(request, error));
  }

  static async setPreferredPaymentMethod(
    request: SetPreferredPaymentMethodRequest
  ): Promise<SetPreferredPaymentMethodResponse> {
    const { paymentMethodId } = request;
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.PaymentMethodSetPreferred(paymentMethodId), {}, config)
      .then(httpResponse => {
        const response = this.prepareResponse<SetPreferredPaymentMethodResponse>(request, httpResponse);
        if (response.success) response.paymentMethod = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<SetPreferredPaymentMethodResponse>(request, error));
  }

  static async updateManualPaymentMethod(
    request: UpdateManualPaymentMethodRequest
  ): Promise<UpdateManualPaymentMethodResponse> {
    const { paymentMethodId, ...data } = request;
    const { client, config } = this.createClient();

    return client
      .put(NexusRoutes.PaymentMethodDetail(paymentMethodId), data, config)
      .then(httpResponse => {
        const response = this.prepareResponse<UpdateManualPaymentMethodResponse>(request, httpResponse);
        if (response.success) response.paymentMethod = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<UpdateManualPaymentMethodResponse>(request, error));
  }

  static async updateOrder(request: UpdateOrderRequest): Promise<UpdateOrderResponse> {
    const { orderId, ...data } = request;
    const { client, config } = this.createClient();

    return client
      .put(NexusRoutes.Order(orderId), data, config)
      .then(httpResponse => {
        const response = this.prepareResponse<UpdateOrderResponse>(request, httpResponse);
        if (response.success) response.order = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<UpdateOrderResponse>(request, error));
  }

  static async createPlaidBankAccount(request: CreatePlaidBankAccountRequest): Promise<CreatePlaidBankAccountResponse> {
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.PlaidBankAccounts, request, config)
      .then(httpResponse => {
        const response = this.prepareResponse<CreatePlaidBankAccountResponse>(request, httpResponse);
        if (response.success) response.bank_account = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<CreatePlaidBankAccountResponse>(request, error));
  }

  static async fetchPlaidLinkToken(request: FetchPlaidLinkTokenRequest): Promise<FetchPlaidLinkTokenResponse> {
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.PlaidLinkToken, {}, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchPlaidLinkTokenResponse>(request, httpResponse);
        if (response.success) {
          response.link_token = httpResponse.data.link_token;
          response.expiration = httpResponse.data.expiration;
          response.request_id = httpResponse.data.request_id;
        }
        return response;
      })
      .catch(error => this.prepareResponse<FetchPlaidLinkTokenResponse>(request, error));
  }

  static async fetchIdentityVerification(
    request: FetchIdentityVerificationRequest
  ): Promise<FetchIdentityVerificationResponse> {
    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.IdentityVerification, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchIdentityVerificationResponse>(request, httpResponse);
        if (response.success) response.verification = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<FetchIdentityVerificationResponse>(request, error));
  }

  static async startIdentityVerification(
    request: StartIdentityVerificationRequest
  ): Promise<StartIdentityVerificationResponse> {
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.IdentityVerification, { force_new: request.force_new }, config)
      .then(httpResponse => {
        const response = this.prepareResponse<StartIdentityVerificationResponse>(request, httpResponse);
        if (response.success) {
          response.verification_id = httpResponse.data.verification_id;
          response.link_token = httpResponse.data.link_token;
        }
        return response;
      })
      .catch(error => this.prepareResponse<StartIdentityVerificationResponse>(request, error));
  }

  static async syncIdentityVerification(
    request: SyncIdentityVerificationRequest
  ): Promise<SyncIdentityVerificationResponse> {
    const { verificationId } = request;
    const { client, config } = this.createClient();

    return client
      .post(NexusRoutes.IdentityVerificationSync(verificationId), {}, config)
      .then(httpResponse => {
        const response = this.prepareResponse<SyncIdentityVerificationResponse>(request, httpResponse);
        if (response.success) response.verification = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse<SyncIdentityVerificationResponse>(request, error));
  }

  static async getClaim(request: GetClaimRequest): Promise<GetClaimResponse> {
    const { client, config } = this.createClient();
    return client
      .get(`${NexusRoutes.GromaCoinClaim}/${request.claimId}/`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<GetClaimResponse>(request, httpResponse);
        if (response.success) response.claim = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse(request, error));
  }

  static async claimCoin(request: ClaimCoinRequest): Promise<ClaimCoinResponse> {
    const { client, config } = this.createClient();
    return client
      .patch(`${NexusRoutes.GromaCoinClaim}/${request.claimId}/`, { email: request.email }, config)
      .then(httpResponse => {
        const response = this.prepareResponse<ClaimCoinResponse>(request, httpResponse);
        if (response.success) response.claim = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse(request, error));
  }

  static async getOverview(request: GetOverviewRequest): Promise<GetOverviewResponse> {
    const { client, config } = this.createClient();
    return client
      .get(`${NexusRoutes.GromaCoinOverview}/`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<GetOverviewResponse>(request, httpResponse);
        if (response.success) response.overview = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse(request, error));
  }

  static async requestInvitation(request: RequestInvitationRequest): Promise<RequestInvitationResponse> {
    const { client, config } = this.createClient();
    return client
      .post(`${NexusRoutes.GromaCoinClaim}/${request.claimId}/request-invitation/`, {}, config)
      .then(httpResponse => {
        const response = this.prepareResponse<RequestInvitationResponse>(request, httpResponse);
        if (response.success) response.message = httpResponse.data.message;
        return response;
      })
      .catch(error => this.prepareResponse(request, error));
  }

  static async getEcosystemOverview(request: GetEcosystemOverviewRequest): Promise<GetEcosystemOverviewResponse> {
    const { client, config } = this.createClient();
    return client
      .get(`${NexusRoutes.GromaCoinEcosystemOverview}/`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<GetEcosystemOverviewResponse>(request, httpResponse);
        if (response.success) response.ecosystemOverview = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse(request, error));
  }

  static async verifyClaimOwner(request: VerifyClaimOwnerRequest): Promise<VerifyClaimOwnerResponse> {
    const { client, config } = this.createClient();
    return client
      .post(`${NexusRoutes.GromaCoinClaim}/${request.claimId}/verify-owner/`, { email: request.email }, config)
      .then(httpResponse => {
        const response = this.prepareResponse<VerifyClaimOwnerResponse>(request, httpResponse);
        if (response.success) response.claim = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse(request, error));
  }

  static async joinWaitlist(request: JoinWaitlistRequest): Promise<JoinWaitlistResponse> {
    const { client, config } = this.createClient();
    return client
      .post(
        `${NexusRoutes.GromaCoinWaitlist}/`,
        { email: request.email, claim_id: request.claimId, source: request.source },
        config
      )
      .then(httpResponse => {
        const response = this.prepareResponse<JoinWaitlistResponse>(request, httpResponse);
        if (response.success) response.entry = httpResponse.data;
        return response;
      })
      .catch(error => this.prepareResponse(request, error));
  }
}
